import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import barStockConsumptionReportService from '../services/barStockConsumptionReportService';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Calendar, Download, FileSpreadsheet, Loader2, RefreshCw, Search } from 'lucide-react';
import { Switch } from '../components/ui/switch';

const formatNumber = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return num.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
};

const getDefaultDateRange = () => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setMonth(startDate.getMonth() - 1);
  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
  };
};

const BarStockConsumptionReportPage = () => {
  const [dateRange, setDateRange] = useState(getDefaultDateRange());
  const [filters, setFilters] = useState({
    client_id: '',
    supplier_id: '',
    product_id: '',
    include_non_purchase_sources: false,
  });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 100;

  const loadReport = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await barStockConsumptionReportService.getReport(
        dateRange.startDate,
        dateRange.endDate,
        filters,
        page,
        pageSize
      );
      setReport(data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || '加载报表失败');
    } finally {
      setLoading(false);
    }
  }, [dateRange, filters, page]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSwitchChange = (name) => (checked) => {
    setFilters((prev) => ({ ...prev, [name]: checked }));
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = () => {
    setPage(1);
    loadReport();
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      await barStockConsumptionReportService.exportReport(
        dateRange.startDate,
        dateRange.endDate,
        filters
      );
    } catch (err) {
      setError(err.response?.data?.error || err.message || '导出失败');
    } finally {
      setExporting(false);
    }
  };

  const rows = report?.rows || [];
  const summary = report?.summary || {};
  const pagination = report?.pagination || { page: 1, page_size: pageSize, total: 0, total_pages: 1 };

  return (
    <Layout title="棒料采购消耗报表" activeTab="reports">
      <div className="max-w-[1600px] mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground">棒料采购消耗报表</h1>
            <p className="text-sm text-muted-foreground mt-1">
              按采购合同追踪棒料的入库、领用消耗与库存情况
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleSearch}
              disabled={loading}
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              <span className="ml-2">刷新</span>
            </Button>
            <Button
              onClick={handleExport}
              disabled={exporting || rows.length === 0}
            >
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              <span className="ml-2">导出 Excel</span>
            </Button>
          </div>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <Search className="h-4 w-4" />
              查询条件
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startDate">开始日期</Label>
                <div className="relative">
                  <Calendar className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="startDate"
                    name="startDate"
                    type="date"
                    value={dateRange.startDate}
                    onChange={handleDateChange}
                    className="pl-9"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="endDate">结束日期</Label>
                <div className="relative">
                  <Calendar className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="endDate"
                    name="endDate"
                    type="date"
                    value={dateRange.endDate}
                    onChange={handleDateChange}
                    className="pl-9"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="client_id">客户</Label>
                <Input
                  id="client_id"
                  name="client_id"
                  placeholder="客户 ID"
                  value={filters.client_id}
                  onChange={handleFilterChange}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="supplier_id">供应商</Label>
                <Input
                  id="supplier_id"
                  name="supplier_id"
                  placeholder="供应商 ID"
                  value={filters.supplier_id}
                  onChange={handleFilterChange}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="product_id">品名/物料编码</Label>
                <Input
                  id="product_id"
                  name="product_id"
                  placeholder="物料编码"
                  value={filters.product_id}
                  onChange={handleFilterChange}
                />
              </div>
              <div className="space-y-2 flex flex-col justify-center">
                <Label htmlFor="include_non_purchase_sources" className="cursor-pointer">
                  包含非采购来源
                </Label>
                <div className="flex items-center space-x-2 h-10">
                  <Switch
                    id="include_non_purchase_sources"
                    checked={filters.include_non_purchase_sources}
                    onCheckedChange={handleSwitchChange('include_non_purchase_sources')}
                  />
                  <span className="text-sm text-muted-foreground">
                    余料/手动入库/外协/调拨等
                  </span>
                </div>
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <Button onClick={handleSearch} disabled={loading}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                <span className="ml-2">生成报表</span>
              </Button>
            </div>
          </CardContent>
        </Card>

        {error && (
          <div className="mb-4 p-4 rounded-md bg-destructive/10 text-destructive text-sm">
            {error}
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <FileSpreadsheet className="h-4 w-4" />
              报表数据
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="whitespace-nowrap">来源类型</TableHead>
                    <TableHead className="whitespace-nowrap">发票日期</TableHead>
                    <TableHead className="whitespace-nowrap">入库日期</TableHead>
                    <TableHead className="whitespace-nowrap">采购单号</TableHead>
                    <TableHead className="whitespace-nowrap">采购单编号</TableHead>
                    <TableHead className="whitespace-nowrap">品牌</TableHead>
                    <TableHead className="whitespace-nowrap">品名</TableHead>
                    <TableHead className="whitespace-nowrap">specification</TableHead>
                    <TableHead className="text-right whitespace-nowrap">本期购入数量</TableHead>
                    <TableHead className="text-right whitespace-nowrap">本期购入单价(未税)</TableHead>
                    <TableHead className="text-right whitespace-nowrap">本期购入总价(未税)</TableHead>
                    <TableHead className="text-right whitespace-nowrap">本期领用数量</TableHead>
                    <TableHead className="text-right whitespace-nowrap">本期领用单价(未税)</TableHead>
                    <TableHead className="text-right whitespace-nowrap">本期领用总价(未税)</TableHead>
                    <TableHead className="text-right whitespace-nowrap">本期库存数量</TableHead>
                    <TableHead className="text-right whitespace-nowrap">本期库存单价(未税)</TableHead>
                    <TableHead className="text-right whitespace-nowrap">本期库存总价(未税)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={17} className="text-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                        <p className="text-sm text-muted-foreground mt-2">加载中...</p>
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading && rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={17} className="text-center py-8 text-muted-foreground">
                        没有找到符合条件的记录
                      </TableCell>
                    </TableRow>
                  )}
                  {rows.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell className="whitespace-nowrap">
                        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-muted">
                          {row.source_label || '采购入库'}
                        </span>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">{row.invoice_date}</TableCell>
                      <TableCell className="whitespace-nowrap">{row.receipt_date}</TableCell>
                      <TableCell className="whitespace-nowrap">{row.purchase_order_number}</TableCell>
                      <TableCell className="whitespace-nowrap">{row.purchase_order_code}</TableCell>
                      <TableCell className="whitespace-nowrap">{row.brand}</TableCell>
                      <TableCell className="whitespace-nowrap">{row.product_name}</TableCell>
                      <TableCell className="whitespace-nowrap">{row.specification}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">{formatNumber(row.purchase_quantity)}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">{formatNumber(row.purchase_unit_price)}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">{formatNumber(row.purchase_total)}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">{formatNumber(row.consumption_quantity)}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">{formatNumber(row.consumption_unit_price)}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">{formatNumber(row.consumption_total)}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">{formatNumber(row.inventory_quantity)}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">{formatNumber(row.inventory_unit_price)}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">{formatNumber(row.inventory_total)}</TableCell>
                    </TableRow>
                  ))}
                  {rows.length > 0 && (
                    <TableRow className="font-semibold bg-muted/30">
                      <TableCell colSpan={8} className="text-right">合计</TableCell>
                      <TableCell className="text-right whitespace-nowrap">{formatNumber(summary.total_purchase_quantity)}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">-</TableCell>
                      <TableCell className="text-right whitespace-nowrap">{formatNumber(summary.total_purchase_total)}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">{formatNumber(summary.total_consumption_quantity)}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">-</TableCell>
                      <TableCell className="text-right whitespace-nowrap">{formatNumber(summary.total_consumption_total)}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">{formatNumber(summary.total_inventory_quantity)}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">-</TableCell>
                      <TableCell className="text-right whitespace-nowrap">{formatNumber(summary.total_inventory_total)}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            {pagination.total_pages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-muted-foreground">
                  第 {pagination.page} / {pagination.total_pages} 页，共 {pagination.total} 条
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1 || loading}
                  >
                    上一页
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(pagination.total_pages, p + 1))}
                    disabled={page >= pagination.total_pages || loading}
                  >
                    下一页
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default BarStockConsumptionReportPage;
